import { environment } from '@root/src/environments/environment';
import { IMsfFormBuilder } from '@components/form-builder/form-builder';
import {ILanguage} from '@components/select-language';

// Локальный интерфейс для совместимости
interface FilterItem extends IMsfFormBuilder {
    metaField?: string;
}

export class MultilingualFieldGenerator {

    /**
     * Определяет, является ли поле многоязычным.
     * Многоязычными считаются только поля с именем "names".
     * Обычное поле "name" трактуем как однозначное строковое,
     * чтобы не ломать словари вроде тегов, где нет LocalizationString.
     */
    static isMultilingualField(fieldName: string): boolean {
        return fieldName === 'names';
    }

    /**
     * Генерирует поля для всех поддерживаемых языков
     */
    static generateMultilingualFields(baseField: FilterItem): FilterItem[] {
        const multilingualFields: FilterItem[] = [];

        environment.langs.forEach((langObj: ILanguage) => {
          const  lang = langObj.value;
            const langField: FilterItem = {
                ...baseField,
                name: this.getFieldNameForLanguage(baseField.name, lang),
                title: `Name (${this.getLanguageLabel(lang)})`,
                type: 'input',
                placeholder: `Enter name (${this.getLanguageLabel(lang)})`
            };

            multilingualFields.push(langField);
        });

        return multilingualFields;
    }

    /**
     * Получает название поля для конкретного языка
     */
    static getFieldNameForLanguage(baseFieldName: string, lang: string): string {
        const langMap: { [key: string]: string } = {
            'en-US': 'En',
            'ru-RU': 'Ru',
            'uz-Latn-UZ': 'UZ'
        };

        const langSuffix = langMap[lang] || lang;
        return `${baseFieldName}${langSuffix}`;
    }

    /**
     * Получает метку языка для отображения
     */
    static getLanguageLabel(lang: string): string {
        const labels: { [key: string]: string } = {
            'en-US': 'EN',
            'ru-RU': 'RU',
            'uz-Latn-UZ': 'UZ'
        };

        return labels[lang] || lang;
    }

    /**
     * Трансформирует данные формы в формат API (создание/обновление)
     */
    static transformDataForSave(modalData: any): any {
        const transformedData = { ...modalData };

        // Проверяем наличие многоязычных полей
        const multilingualValues: Record<string, string> = {};
        let hasAnyValue = false;

        environment.langs.forEach(langObj => {
          const lang =  langObj.value;
            const fieldName = this.getFieldNameForLanguage('names', lang);
            const value = modalData[fieldName];

            if (value !== null && value !== undefined && value !== '') {
                const trimmedValue = String(value).trim();
                if (trimmedValue) {
                    multilingualValues[lang] = trimmedValue;
                    hasAnyValue = true;
                }
                delete transformedData[fieldName];
            }
        });

        // Создаем объект names только если есть хотя бы одно значение
        if (hasAnyValue) {
            transformedData.names = multilingualValues;
        }

        return transformedData;
    }

    /**
     * Трансформирует данные API в формат формы (для редактирования)
     */
    static transformDataForEdit(item: any): any {
        const transformedData = { ...item };

        // Если есть объект names, извлекаем значения для каждого языка
        if (item.names && typeof item.names === 'object') {
            environment.langs.forEach(langObj => {
              const lang =  langObj.value;
              const fieldName = this.getFieldNameForLanguage('names', lang);

                if (item.names[lang]) {
                    transformedData[fieldName] = item.names[lang];
                }
            });

            // Удаляем объект names, так как мы извлекли значения
            delete transformedData.names;
        }

        return transformedData;
    }

    /**
     * Проверяет, есть ли в данных многоязычные поля
     */
    static hasMultilingualData(data: any): boolean {
        return data && data.names && typeof data.names === 'object';
    }

    /**
     * Получает значение для текущего языка
     */
    static getNameForLanguage(item: any, currentLang: string): string {
        if (!item.names || typeof item.names !== 'object') {
            return item.name || '';
        }

        // Пробуем получить значение для текущего языка
        if (item.names[currentLang]) {
            return item.names[currentLang];
        }

        // Fallback на другие языки
        const fallbackOrder = ['en-US', 'ru-RU', 'uz-Latn-UZ'];
        for (const lang of fallbackOrder) {
            if (item.names[lang]) {
                return item.names[lang];
            }
        }

        return '';
    }
}
